import { useCallback } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Button } from '@antimatterhq/ui/components/button';
import { Label } from '@antimatterhq/ui/components/label';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import { Section } from '@/components/container/section/Section';
import {
  JSONExtractionSchemaValues,
  JSONExtractionsSchemaValues,
} from '@/zod/schemas/dataSource/Common';
import { FormInput } from '@/components/form/input/FormInput';
import { FormError } from '@/components/form/form';
import { FormValues } from '@/components/form/values/FormValues';
import { collidingJsonTooltip } from '@/strings/StringsTooltips';

const emptyJsonExtract: JSONExtractionSchemaValues = {
  source: '',
  omitFields: [''],
  duplicatePrefix: '',
  embed: false,
  embedColumn: '',
};

export type JSONExtractionProps = {
  prefix?: string;
  jsonExtract: JSONExtractionSchemaValues[];
  disabled?: boolean;
};

export const JSONExtraction = ({
  prefix = '',
  jsonExtract = [],
  disabled = false,
}: JSONExtractionProps) => {
  const formik = useFormikContext<JSONExtractionsSchemaValues>();
  const { handleChange, isSubmitting, handleBlur } = formik;

  const createJSONExtraction = useCallback(() => ({ ...emptyJsonExtract }), []);

  return (
    <div>
      <FieldArray name={`${prefix}jsonExtract`}>
        {({ remove, push }) => (
          <>
            {jsonExtract.map((extract, index) => (
              <div key={`${prefix}jsonExtract.${index}`}>
                <Spacer size="lg" />

                <Section className="relative">
                  <Spacer size="lg" />

                  <Label
                    htmlFor={`${prefix}jsonExtract.${index}.source`}
                    id={`${prefix}jsonExtract.${index}.source-label`}
                    required
                  >
                    Column to extract
                  </Label>

                  <FormInput
                    name={`${prefix}jsonExtract.${index}.source`}
                    id={`${prefix}jsonExtract.${index}.source-input`}
                    data-testid={`${prefix}jsonExtract.${index}.source-input`}
                    value={extract.source ?? undefined}
                    onChange={handleChange}
                    disabled={disabled}
                    placeholder="Column name"
                    onBlur={handleBlur}
                    aria-labelledby={`${prefix}jsonExtract.${index}.source-label`}
                  />

                  <FormError name={`${prefix}jsonExtract.${index}.source`} />

                  <Spacer size="lg" />

                  <Label>Fields to omit</Label>
                  <FormValues
                    name={`${prefix}jsonExtract.${index}.omitFields`}
                    values={extract.omitFields}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={disabled}
                  />

                  <Spacer size="lg" />

                  <Label
                    htmlFor={`${prefix}jsonExtract.${index}.duplicatePrefix`}
                    id={`${prefix}jsonExtract.${index}.duplicatePrefix-label`}
                    info={collidingJsonTooltip}
                  >
                    Embed the extracted fields into a new column
                  </Label>

                  <ButtonGroup
                    options={[
                      {
                        label: 'No',
                        value: 'no',
                      },
                      {
                        label: 'Yes',
                        value: 'yes',
                      },
                    ]}
                    value={extract.embed ? 'yes' : 'no'}
                    onChange={(value) => {
                      handleChange({
                        target: {
                          name: `${prefix}jsonExtract.${index}.embed`,
                          value: value === 'yes',
                        },
                      });
                    }}
                    disabled={isSubmitting || disabled}
                    data-testid={`json-extract-embed-${index}`}
                    aria-labelledby={`${prefix}jsonExtract.${index}.duplicatePrefix-label`}
                  />

                  {!extract.embed && (
                    <>
                      <Spacer size="lg" />

                      <Label>Prefix for colliding column names</Label>

                      <FormInput
                        name={`${prefix}jsonExtract.${index}.duplicatePrefix`}
                        value={extract.duplicatePrefix ?? undefined}
                        onChange={handleChange}
                        disabled={disabled}
                        placeholder="Column name prefix"
                        onBlur={handleBlur}
                      />
                      <FormError
                        name={`${prefix}jsonExtract.${index}.duplicatePrefix`}
                      />
                    </>
                  )}

                  {extract.embed && (
                    <>
                      <Spacer size="lg" />

                      <Label
                        htmlFor={`${prefix}jsonExtract.${index}.embedColumn`}
                        id={`${prefix}jsonExtract.${index}.embedColumn-label`}
                        required
                      >
                        Destination column
                      </Label>

                      <FormInput
                        name={`${prefix}jsonExtract.${index}.embedColumn`}
                        value={extract.embedColumn ?? undefined}
                        onChange={handleChange}
                        disabled={disabled}
                        placeholder="New column name"
                        onBlur={handleBlur}
                        aria-labelledby={`${prefix}jsonExtract.${index}.embedColumn-label`}
                      />
                      <FormError
                        name={`${prefix}jsonExtract.${index}.embedColumn`}
                      />
                    </>
                  )}

                  <div className="absolute right-2 top-2">
                    <Button
                      variant="ghost"
                      size="small"
                      iconOnly
                      leftIcon="antimatterhq:trash"
                      onClick={() => remove(index)}
                      disabled={isSubmitting || disabled}
                    />
                  </div>
                </Section>
              </div>
            ))}

            <Spacer size="lg" />

            <Button
              variant="default"
              onClick={() => push(createJSONExtraction())}
              disabled={isSubmitting || disabled}
            >
              Add a column to extract
            </Button>
          </>
        )}
      </FieldArray>
    </div>
  );
};
